import { type FormEvent, type ReactNode, useState } from 'react';

import type { RouteComponentProps, RouteElement } from '@shared/router';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';
import RouteNotFound from '@shared/views/bootstrap/RouteNotFound';

import { Badge } from '@playbook/atoms/badge';
import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';

import { ApiError } from '@portal/pkg/api';
import { useKeyActions } from '@portal/pkg/api/keys';
import { Action } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import { Notice } from '@portal/views/_shared/notice';
import { PageHeader } from '@portal/views/_shared/page';
import { CapabilityPicker } from '@portal/views/keys/CapabilityPicker';
import { KeyPageFrame, useKey } from '@portal/views/keys/KeyPageFrame';
import { NotPermitted, can, isActive } from '@portal/views/keys/_shared';

import css from './styles.module.css';

export default function EditAccessPage({
	params,
}: RouteComponentProps): RouteElement {
	return (
		<KeyPageFrame>
			<EditAccess id={params?.['key']?.toString()} />
		</KeyPageFrame>
	);
}

function EditAccess({ id }: { id: Optional<string> }): ReactNode {
	const key = useKey(id);
	const { updateCapabilities } = useKeyActions();

	const [selected, setSelected] = useState<Nullable<string[]>>(null);
	const [error, setError] = useState<Nullable<string>>(null);
	const [saving, setSaving] = useState(false);

	if (!key) {
		return <RouteNotFound />;
	}

	// The action is absent on a key that is revoked or expired, which the
	// server decides — so this covers both "you may not" and "not on this
	// key", and says which.
	if (!can(key, Action.UpdateKey)) {
		return isActive(key) ? (
			<NotPermitted what="change this key's access" />
		) : (
			<Notice variant="information" title="This key is no longer active">
				{key.status === 'revoked'
					? 'A revoked key cannot be changed.'
					: 'An expired key cannot be changed. Rotate it to get a working one.'}
			</Notice>
		);
	}

	// Seeded from the key on first render, and the person's own choice after.
	const capabilities = selected ?? key.capabilities;

	const added = capabilities.filter(
		(capability) => !key.capabilities.includes(capability)
	);
	const removed = key.capabilities.filter(
		(capability) => !capabilities.includes(capability)
	);
	const changed = added.length > 0 || removed.length > 0;

	async function handleSubmit(event: FormEvent): Promise<void> {
		event.preventDefault();

		if (!key) {
			return;
		}

		setError(null);
		setSaving(true);

		try {
			await updateCapabilities(key.id, capabilities);

			history.push(routes.Key(key.id));
		} catch (caught) {
			setError(
				caught instanceof ApiError
					? caught.message
					: 'Something went wrong. Try again.'
			);
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className={css.page}>
			<PageHeader
				title="Edit access"
				description="The key keeps working throughout."
				breadcrumb={
					<Link href={routes.Key(key.id)}>
						<span className={css.breadcrumb}>← {key.name}</span>
					</Link>
				}
			/>

			<Card>
				<form className={css.form} onSubmit={handleSubmit}>
					{error && (
						<Notice variant="error" title="Access was not changed">
							{error}
						</Notice>
					)}

					<CapabilityPicker
						selected={capabilities}
						label="Access"
						onChange={setSelected}
					/>

					{changed && (
						<div className={css.diff}>
							<span className={css.diffLabel}>Changing</span>
							{removed.map((capability) => (
								<Badge
									key={capability}
									variant="error"
									label={`− ${capability}`}
								/>
							))}
							{added.map((capability) => (
								<Badge
									key={capability}
									variant="success"
									label={`+ ${capability}`}
								/>
							))}
						</div>
					)}

					<div className={css.explain}>
						<h3>When it takes effect</h3>
						<ul>
							<li>
								<strong>Taking access away</strong> bites on the key's very next
								call. A token already issued cannot use what the key no longer
								has.
							</li>
							<li>
								<strong>Giving access</strong> applies to the next token. The
								integration names its scope when it asks for one, so it picks
								the new access up on its next exchange.
							</li>
						</ul>
						<p className={css.quiet}>
							Whoever runs this integration will not be told. The change is
							recorded under Activity with your name on it.
						</p>
					</div>

					<div className={css.actions}>
						<Button
							label="Cancel"
							onClick={() => history.push(routes.Key(key.id))}
						/>
						<Button
							variant="primary"
							type="submit"
							label="Save access"
							loading={saving}
							disabled={saving || !changed || capabilities.length === 0}
						/>
					</div>
				</form>
			</Card>
		</div>
	);
}
